/**
 * Technology System - manages tech research and effects for factions
 * Supports complex tech tree with mutual exclusion, alternative prerequisites, etc.
 */

import { EventBus } from '@/core/events/EventBus';
import type { FactionManager } from '@/game/factions/FactionManager';
import type { FactionId } from '@/types';
import type { Tech, TechId, TechEffect, TechStatus, TechBranch, TechBonuses } from './TechTypes';
import { getTech, ALL_TECHS, getTechsByBranch } from './data';

export interface ResearchProgress {
  techId: TechId;
  progress: number; // 0 to 1
  startTime: number;
}

export interface FactionTechState {
  researched: Set<TechId>;
  lockedTechs: Set<TechId>; // Techs locked due to mutual exclusion
  currentResearch: ResearchProgress | null;
  effects: TechBonuses; // Combined effects from all researched techs
}

export class TechSystem {
  private factionManager: FactionManager;
  private factionStates: Map<FactionId, FactionTechState> = new Map();
  private gameTime: number = 0;

  constructor(factionManager: FactionManager) {
    this.factionManager = factionManager;
  }

  /**
   * Initialize tech state for a faction
   */
  initFaction(factionId: FactionId): void {
    this.factionStates.set(factionId, {
      researched: new Set(),
      lockedTechs: new Set(),
      currentResearch: null,
      effects: this.createEmptyEffects(),
    });
  }

  /**
   * Update research progress
   */
  update(deltaTime: number): void {
    this.gameTime += deltaTime;

    for (const [factionId, state] of this.factionStates) {
      if (!state.currentResearch) continue;

      const tech = getTech(state.currentResearch.techId);
      if (!tech) continue;

      // Apply research speed bonus
      // deltaTime is in seconds, researchTime is in minutes
      const speedBonus = state.effects.researchSpeedBonus || 1;
      const researchTimeSeconds = tech.researchTime * 60;
      state.currentResearch.progress += (deltaTime / researchTimeSeconds) * speedBonus;

      if (state.currentResearch.progress >= 1) {
        this.completeResearch(factionId);
      }
    }
  }

  /**
   * Check if a faction can research a specific tech
   */
  canResearch(factionId: FactionId, techId: TechId): { can: boolean; reason?: string } {
    const state = this.factionStates.get(factionId);
    if (!state) return { can: false, reason: 'Faction not initialized' };

    // Already researching something
    if (state.currentResearch) {
      return { can: false, reason: 'Already researching' };
    }

    // Already researched
    if (state.researched.has(techId)) {
      return { can: false, reason: 'Already researched' };
    }

    // Locked due to mutual exclusion
    if (state.lockedTechs.has(techId)) {
      return { can: false, reason: 'Mutually exclusive tech already researched' };
    }

    const tech = getTech(techId);
    if (!tech) return { can: false, reason: 'Tech not found' };

    // Check faction exclusivity
    if (tech.factionExclusive && tech.factionExclusive !== factionId) {
      return { can: false, reason: 'Not available for this faction' };
    }

    // Check faction requirements
    if (tech.factionRequired && !tech.factionRequired.includes(factionId)) {
      return { can: false, reason: 'Not available for this faction' };
    }

    // Check prerequisites
    if (!this.checkPrerequisites(factionId, tech)) {
      return { can: false, reason: 'Prerequisites not met' };
    }

    // Check cost
    const gold = this.factionManager.getGold(factionId);
    if (gold < tech.cost) {
      return { can: false, reason: 'Insufficient gold' };
    }

    return { can: true };
  }

  /**
   * Check if prerequisites are met (supports alternative prerequisites)
   */
  private checkPrerequisites(factionId: FactionId, tech: Tech): boolean {
    const state = this.factionStates.get(factionId);
    if (!state) return false;

    // Check main prerequisites (all must be completed)
    for (const prereq of tech.prerequisites) {
      if (!state.researched.has(prereq)) {
        // Check if this is satisfied by alternative prereqs
        if (!tech.alternativePrereqs) return false;
      }
    }

    // If main prerequisites are all met, we're good
    const mainPrereqsMet = tech.prerequisites.every(p => state.researched.has(p));
    if (mainPrereqsMet && tech.prerequisites.length > 0) {
      return true;
    }

    // If no main prerequisites, check alternatives
    if (tech.prerequisites.length === 0 && !tech.alternativePrereqs) {
      return true; // No prerequisites required
    }

    // Check alternative prerequisite groups (any group can satisfy)
    if (tech.alternativePrereqs) {
      for (const group of tech.alternativePrereqs) {
        const groupMet = group.every(prereqId => state.researched.has(prereqId));
        if (groupMet) return true;
      }
    }

    // If main prereqs not met and no alternative satisfied, check if main prereqs exist
    return mainPrereqsMet;
  }

  /**
   * Start researching a technology
   */
  startResearch(factionId: FactionId, techId: TechId): boolean {
    const canResult = this.canResearch(factionId, techId);
    if (!canResult.can) return false;

    const state = this.factionStates.get(factionId)!;
    const tech = getTech(techId)!;

    // Deduct cost
    if (!this.factionManager.spendGold(factionId, tech.cost)) return false;

    state.currentResearch = {
      techId,
      progress: 0,
      startTime: this.gameTime,
    };

    EventBus.emit('tech:started', { factionId, techId });

    return true;
  }

  /**
   * Cancel current research (no refund)
   */
  cancelResearch(factionId: FactionId): boolean {
    const state = this.factionStates.get(factionId);
    if (!state || !state.currentResearch) return false;

    state.currentResearch = null;
    return true;
  }

  /**
   * Calculate cost to speed up (complete instantly) current research
   * Cost = (remaining time * 50) + (tech base cost * remaining progress * 0.5)
   */
  getSpeedUpCost(factionId: FactionId): number {
    const state = this.factionStates.get(factionId);
    if (!state || !state.currentResearch) return 0;

    const tech = getTech(state.currentResearch.techId);
    if (!tech) return 0;

    const remainingProgress = 1 - state.currentResearch.progress;
    const totalTimeMinutes = tech.researchTime;
    const remainingMinutes = totalTimeMinutes * remainingProgress;

    // 50 gold per minute + 50% of remaining tech cost, minimum 50 gold
    const timeCost = remainingMinutes * 50;
    const baseCost = tech.cost * remainingProgress * 0.5;
    return Math.max(50, Math.ceil(timeCost + baseCost));
  }

  /**
   * Speed up research by spending gold (completes instantly)
   */
  speedUpResearch(factionId: FactionId): boolean {
    const state = this.factionStates.get(factionId);
    if (!state || !state.currentResearch) return false;

    const cost = this.getSpeedUpCost(factionId);
    if (cost <= 0) return false;

    // Check and spend gold
    if (!this.factionManager.spendGold(factionId, cost)) return false;

    const techId = state.currentResearch.techId;

    // Complete the research instantly
    state.currentResearch.progress = 1;
    this.completeResearch(factionId);

    EventBus.emit('tech:speedup', { factionId, techId, cost });

    return true;
  }

  /**
   * Complete the current research
   */
  private completeResearch(factionId: FactionId): void {
    const state = this.factionStates.get(factionId);
    if (!state || !state.currentResearch) return;

    const techId = state.currentResearch.techId;
    const tech = getTech(techId);
    if (!tech) return;

    state.researched.add(techId);
    state.currentResearch = null;

    // Handle mutual exclusion - lock exclusive techs
    this.handleMutualExclusion(factionId, tech);

    // Recalculate combined effects
    this.recalculateEffects(factionId);

    EventBus.emit('tech:completed', { factionId, techId, techName: tech.name });
  }

  /**
   * Lock mutually exclusive techs when a tech is completed
   */
  private handleMutualExclusion(factionId: FactionId, completedTech: Tech): void {
    const state = this.factionStates.get(factionId);
    if (!state) return;

    if (completedTech.mutuallyExclusive) {
      for (const exclusiveId of completedTech.mutuallyExclusive) {
        state.lockedTechs.add(exclusiveId);
      }
    }
  }

  /**
   * Recalculate combined effects from all researched techs
   */
  private recalculateEffects(factionId: FactionId): void {
    const state = this.factionStates.get(factionId);
    if (!state) return;

    const effects = this.createEmptyEffects();

    for (const techId of state.researched) {
      const tech = getTech(techId);
      if (!tech?.effects?.bonuses) continue;

      const bonuses = tech.effects.bonuses;

      // Combine multiplicative bonuses
      const multiplicativeKeys: (keyof TechBonuses)[] = [
        'attackBonus', 'defenseBonus', 'speedBonus', 'incomeBonus',
        'trainSpeedBonus', 'buildSpeedBonus', 'researchSpeedBonus',
        'unitHealthBonus', 'marketBonus', 'tradingBonus', 'taxBonus',
        'swordAttack', 'swordDefense', 'archerAttack', 'archerRange',
        'spearAttack', 'spearAntiCav', 'cavalryAttack', 'cavalryCharge',
        'shieldDefense', 'shieldBlock', 'taoistPower', 'taoistHeal',
        'goldCapacity', 'upkeepReduction', 'forestBonus', 'hillBonus',
        'riverCrossing', 'visionRange', 'morale', 'siegeBonus', 'repairSpeed',
      ];

      for (const key of multiplicativeKeys) {
        if (bonuses[key] !== undefined) {
          effects[key] = ((effects[key] as number) || 1) * (bonuses[key] as number);
        }
      }

      // Combine additive bonuses
      const additiveKeys: (keyof TechBonuses)[] = ['farmIncome', 'mineIncome'];
      for (const key of additiveKeys) {
        if (bonuses[key] !== undefined) {
          effects[key] = ((effects[key] as number) || 0) + (bonuses[key] as number);
        }
      }
    }

    state.effects = effects;
  }

  private createEmptyEffects(): TechBonuses {
    return {
      attackBonus: 1,
      defenseBonus: 1,
      speedBonus: 1,
      incomeBonus: 1,
      trainSpeedBonus: 1,
      buildSpeedBonus: 1,
      researchSpeedBonus: 1,
      unitHealthBonus: 1,
      farmIncome: 0,
      mineIncome: 0,
      marketBonus: 1,
      tradingBonus: 1,
      taxBonus: 1,
      morale: 1,
      siegeBonus: 1,
      visionRange: 1,
    };
  }

  /**
   * Get the status of a tech for a faction
   */
  getTechStatus(factionId: FactionId, techId: TechId): TechStatus {
    const state = this.factionStates.get(factionId);
    if (!state) return 'locked';

    // Already researched
    if (state.researched.has(techId)) {
      return 'completed';
    }

    // Currently researching
    if (state.currentResearch?.techId === techId) {
      return 'researching';
    }

    // Locked due to mutual exclusion
    if (state.lockedTechs.has(techId)) {
      return 'excluded';
    }

    // Check if can research
    const canResult = this.canResearch(factionId, techId);
    if (canResult.can || canResult.reason === 'Already researching' || canResult.reason === 'Insufficient gold') {
      return 'available';
    }

    return 'locked';
  }

  /**
   * Get techs for a specific branch, filtered for faction availability
   */
  getTechsForBranch(factionId: FactionId, branch: TechBranch): Tech[] {
    const branchTechs = getTechsByBranch(branch);

    // Filter out faction-exclusive techs that aren't for this faction
    return branchTechs.filter(tech => {
      if (tech.factionExclusive && tech.factionExclusive !== factionId) {
        return false;
      }
      if (tech.factionRequired && !tech.factionRequired.includes(factionId)) {
        return false;
      }
      return true;
    });
  }

  /**
   * Get all available techs for a faction (including faction-specific)
   */
  getAvailableTechs(factionId: FactionId): Tech[] {
    return ALL_TECHS.filter(tech => {
      if (tech.factionExclusive && tech.factionExclusive !== factionId) {
        return false;
      }
      if (tech.factionRequired && !tech.factionRequired.includes(factionId)) {
        return false;
      }
      return true;
    });
  }

  /**
   * Get combined tech effects for a faction
   */
  getEffects(factionId: FactionId): TechBonuses {
    return this.factionStates.get(factionId)?.effects || this.createEmptyEffects();
  }

  /**
   * Check if a faction has researched a tech
   */
  hasResearched(factionId: FactionId, techId: TechId): boolean {
    return this.factionStates.get(factionId)?.researched.has(techId) || false;
  }

  /**
   * Get current research progress
   */
  getCurrentResearch(factionId: FactionId): ResearchProgress | null {
    return this.factionStates.get(factionId)?.currentResearch || null;
  }

  /**
   * Get all researched techs for a faction
   */
  getResearchedTechs(factionId: FactionId): TechId[] {
    const researched = this.factionStates.get(factionId)?.researched;
    return researched ? Array.from(researched) : [];
  }

  /**
   * Get all locked techs for a faction
   */
  getLockedTechs(factionId: FactionId): TechId[] {
    const locked = this.factionStates.get(factionId)?.lockedTechs;
    return locked ? Array.from(locked) : [];
  }

  /**
   * Get techs that can be researched now
   */
  getResearchableTechs(factionId: FactionId): Tech[] {
    const available = this.getAvailableTechs(factionId);
    return available.filter(tech => {
      const status = this.getTechStatus(factionId, tech.id);
      return status === 'available';
    });
  }

  /**
   * Get research progress as percentage (0-100)
   */
  getResearchProgress(factionId: FactionId): number {
    const current = this.factionStates.get(factionId)?.currentResearch;
    return current ? Math.min(current.progress * 100, 100) : 0;
  }

  /**
   * Get count of researched techs
   */
  getResearchedCount(factionId: FactionId): number {
    return this.factionStates.get(factionId)?.researched.size || 0;
  }

  /**
   * Set researched techs directly (for save/load)
   */
  setResearchedTechs(factionId: FactionId, techIds: TechId[]): void {
    const state = this.factionStates.get(factionId);
    if (!state) return;

    state.researched = new Set(techIds);

    // Rebuild locked techs from researched techs
    state.lockedTechs.clear();
    for (const techId of techIds) {
      const tech = getTech(techId);
      if (tech?.mutuallyExclusive) {
        for (const exclusiveId of tech.mutuallyExclusive) {
          state.lockedTechs.add(exclusiveId);
        }
      }
    }

    this.recalculateEffects(factionId);
  }

  /**
   * Set locked techs directly (for save/load)
   */
  setLockedTechs(factionId: FactionId, techIds: TechId[]): void {
    const state = this.factionStates.get(factionId);
    if (!state) return;

    state.lockedTechs = new Set(techIds);
  }

  /**
   * Set current research progress (for save/load)
   */
  setResearchProgress(factionId: FactionId, techId: TechId, progress: number): void {
    const state = this.factionStates.get(factionId);
    if (!state) return;

    state.currentResearch = {
      techId,
      progress,
      startTime: this.gameTime,
    };
  }
}
